import React, { useState, useEffect, useCallback } from "react";
import { Grid, CardHeader, CardContent, Box, TextField, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { ticketManagementServices } from "../../services/AllServices";
import { ticketManagementSchema } from "../../utils/Schema";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { IMAGE_BASE } from "../../utils/constants";

function AddEditLotteryTickets() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState("");

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await ticketManagementServices.getById({ id });
        setPreview(IMAGE_BASE + response?.data?.data.image)
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: ticketManagementSchema,
    initialValues: {
      name: data?.name || "",
      retailerName: data?.retailerName || "",
      startDate: data?.startDate || "",
      endDate: data?.endDate || "",
      ticketAmount: data?.ticketAmount || "",
      winningAmount: data?.winningAmount || "",
      resultDate: data?.resultDate || "",
      image: data?.resultDate || '',
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        if (id) {
          formData.append("ticketId", id || "");
        }
        formData.append("imagePath", "ticket");
        Object.keys(values).forEach((key) => {
          if (values[key]) {
            formData.append(key, values[key]);
          }
        });

        const response = id
          ? await ticketManagementServices.edit(formData)
          : await ticketManagementServices.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate('/tickets-management');
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      const validFormats = ["image/jpeg", "image/png" , "image/jpg"];
      if (validFormats.includes(file.type)) {
        setFieldValue("image", file);
        setPreview(URL.createObjectURL(file));
      } else {
        toast.error("Invalid image format. Only JPG, JPEG, and PNG are allowed.");
      }
    }
  };
  

  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Ticket`} />
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Avatar + Upload Button */}
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      mb={3}
                    >
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: "2px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#e0e0e0",
                          fontSize: 30,
                          color: "#444",
                        }}
                      >
                        {preview ? (
                          <img
                            src={preview}
                            alt="Uploaded"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <img
                            src={'/assets/images/ticket.png'}
                            alt="Uploaded"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        )}

                      </Box>

                      <Button
                        variant="contained"
                        component="label"
                        sx={{
                          mt: 2,
                          backgroundColor: "#2196f3",
                          boxShadow: 3,
                          ":hover": { backgroundColor: "#1976d2" },
                        }}
                      >
                        Upload Ticket Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                      {touched.image && errors.image && (
                        <Box color="error.main" mt={1}>
                          {errors.image}
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Form Fields */}
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Lottery Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Retailer Name"
                      name="retailerName"
                      value={values.retailerName}
                      onChange={handleChange}
                      error={Boolean(
                        touched.retailerName && errors.retailerName
                      )}
                      helperText={touched.retailerName && errors.retailerName}
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MobileDatePicker
                      label="Start Date"
                      inputFormat="YYYY-MM-DD"
                      value={
                        values.startDate ? dayjs(values.startDate) : null
                      }
                      minDate={dayjs()}
                      onChange={(val) =>
                        formik.setFieldValue(
                          "startDate",
                          val ? val.format("YYYY-MM-DD") : ""
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(
                            touched.startDate && errors.startDate
                          ),
                          helperText: touched.startDate && errors.startDate,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MobileDatePicker
                      label="End Date"
                      inputFormat="YYYY-MM-DD"
                      value={values.endDate ? dayjs(values.endDate) : null}
                      minDate={dayjs()}
                      onChange={(val) =>
                        formik.setFieldValue(
                          "endDate",
                          val ? val.format("YYYY-MM-DD") : ""
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.endDate && errors.endDate),
                          helperText: touched.endDate && errors.endDate,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Ticket Purchasing Amount"
                      name="ticketAmount"
                      type="text"
                      value={values.ticketAmount}
                      // onChange={handleChange}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,6}(\.\d{0,2})?$/.test(value) && !value.includes('e')) {
                          handleChange(e);
                        }
                      }}
                      inputProps={{
                        max: 100000,
                        step: "0.01",
                      }}
                      error={Boolean(
                        touched.ticketAmount && errors.ticketAmount
                      )}
                      helperText={
                        touched.ticketAmount && errors.ticketAmount
                      }
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Winning Amount"
                      name="winningAmount"
                      type="text"
                      value={values.winningAmount}
                      // onChange={handleChange}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,6}(\.\d{0,2})?$/.test(value)&& !value.includes('e')) {
                          handleChange(e);
                        }
                      }}
                      inputProps={{
                        max: 100000,
                        step: "0.01",
                      }}
                      error={Boolean(
                        touched.winningAmount && errors.winningAmount
                      )}
                      helperText={
                        touched.winningAmount && errors.winningAmount
                      }
                      required
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MobileDatePicker
                      label="Result Date"
                      inputFormat="YYYY-MM-DD"
                      value={values.resultDate ? dayjs(values.resultDate) : null}
                      minDate={dayjs()}
                      onChange={(val) =>
                        formik.setFieldValue(
                          "resultDate",
                          val ? val.format("YYYY-MM-DD") : ""
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(
                            touched.resultDate && errors.resultDate
                          ),
                          helperText: touched.resultDate && errors.resultDate,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box mt={3}>
                      <LoadingButton
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        loading={loading}
                      >
                        {id ? "Update" : "Create"} Ticket
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </LocalizationProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditLotteryTickets;
